import { z } from 'zod'

import { AUTH_COOKIE_HEADER } from '@/api/auth-cookie'
import { REQUEST_ID_HEADER, newRequestId } from '@/api/request-id'
import { config } from '@/lib/config'
import { clearSession, readSession, writeSession } from '@/lib/auth/session'
import { forgetDevice, readDevice } from '@/lib/push/device-token'

/**
 * What a renewal came back with: a fresh access token, or why there is none.
 * `signed-out`: there is no session to renew. `unavailable`: there may be, but
 * it could not be renewed just now.
 */
export type Renewal = { access: string } | { error: 'signed-out' | 'unavailable' }

/**
 * Why a token is being asked for. `boot`: the tab has no session yet.
 * `expired`: the one it has is spent.
 */
export type RenewReason = 'boot' | 'expired'

/**
 * Where the tab's access token comes from, picked once at boot: mm-be's
 * refresh cookie in a browser tab (`cookieSource`, the default), the Flutter
 * app in its WebView (lib/embed). The store keeps every rule of its own —
 * one refresh in flight, `sessionEpoch`, what a failure ends — so a source
 * answers "what token" and nothing else, and neither rule can drift between
 * the two.
 */
export interface SessionSource {
  /** This tab's token from before a reload, or null. */
  restore: () => string | null
  /** Keeps the token for a reload of this tab, where the source allows it. */
  keep: (access: string) => void
  forget: () => void
  /** Never rejects: a request that fails is a `Renewal` error. */
  renew: (reason: RenewReason) => Promise<Renewal>
  /** The member's own sign-out, at the source. Best effort; never rejects. */
  endSession: () => Promise<void>
}

/**
 * How long a sign-out waits on mm-be before going ahead without it. The
 * member pressed Log out; a server that does not answer must not keep them
 * signed in. The request is not aborted, so a slow answer still clears the
 * cookie when it lands.
 */
const LOGOUT_WAIT_MS = 5_000

/**
 * POST /api/v5/auth/token/refresh/ answers `{ data: { access, refresh } }`,
 * and in cookie mode `refresh` is always null: a rotated refresh token goes
 * into the cookie, never the body. So only `access` is read. Parsed rather
 * than cast: this body arrives through a bare fetch that no generated type
 * guards.
 */
const refreshResponseSchema = z.object({ data: z.object({ access: z.string().min(1) }) })

/**
 * Cookie-mode calls go out on a bare fetch, not the api client: a refresh
 * routed through the client's own 401 handling would recurse, and a logout
 * must not first spend a refresh on the access token it is about to drop.
 * `config.apiPrefix` keeps them on the client's path — relative through the
 * Vite proxy in dev, so the cookie lands on localhost, and absolute against
 * the backend origin in a build. (Chromium and Firefox keep the `Secure`
 * cookie on http://localhost. Safari may refuse it, and a dev session there
 * then ends with its 30-minute access token.)
 */
const cookieRequest = (path: string, body?: string): Promise<Response> =>
  fetch(`${config.apiPrefix}/auth/${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      [AUTH_COOKIE_HEADER]: '1',
      [REQUEST_ID_HEADER]: newRequestId(),
    },
    ...(body === undefined ? {} : { body }),
  })

/**
 * A browser tab: the access token mirrored in sessionStorage (session.ts),
 * renewed from mm-be's HttpOnly refresh cookie.
 */
export const cookieSource: SessionSource = {
  restore: () => readSession()?.access ?? null,
  keep: (access) => {
    writeSession({ access })
  },
  forget: clearSession,

  renew: async () => {
    try {
      const res = await cookieRequest('token/refresh/', '{}')
      // Any failure ends the session — a 401 (no cookie, or a dead one) is
      // the expected one, but a refresh that cannot be trusted is no better —
      // so this keys off `ok`, not off the status or the code. An unreachable
      // mm-be too: the cookie has no "try later" to offer.
      const parsed = res.ok ? refreshResponseSchema.safeParse(await res.json()) : null
      return parsed?.success ? { access: parsed.data.data.access } : { error: 'signed-out' }
    } catch {
      return { error: 'signed-out' }
    }
  },

  endSession: async () => {
    // Best effort, by design: a member who asked to leave is signed out
    // whatever the server says. A cookie that outlives this would sign a new
    // tab back in — worth a trace, not a reason to keep them.
    // With this browser's push token too, so it stops getting this account's
    // pushes; and the token is dropped here, so the next account to sign in
    // starts with push off rather than inheriting it.
    const device = readDevice()
    forgetDevice()
    let timer: ReturnType<typeof setTimeout> | undefined
    try {
      const answer = await Promise.race([
        cookieRequest(
          'logout/',
          device === null ? undefined : JSON.stringify({ fcm_token: device.token }),
        ),
        new Promise<undefined>((resolve) => {
          timer = setTimeout(() => {
            resolve(undefined)
          }, LOGOUT_WAIT_MS)
        }),
      ])
      if (!answer?.ok) {
        console.warn('[auth] mm-be did not confirm the logout', answer?.status ?? 'no answer')
      }
    } catch (error) {
      console.warn('[auth] mm-be could not be reached to log out', error)
    } finally {
      clearTimeout(timer)
    }
  },
}
